<template>
    <v-card class="mt-4">
        <v-card-title>
            <v-row align="center">
                <v-col class="grow ma-2"> Task Groups </v-col>
                <v-col class="shrink text-right">
                    <v-btn
                        v-if="isExperimentTaskGroupList()"
                        color="primary"
                        class="ma-2"
                        @click="showCreateTaskGroupsFromReferencesForm"
                    >
                        Create from References
                    </v-btn>
                    <v-btn color="primary" class="ma-2" @click="showCreateTaskGroupForm">
                        New Task Group
                    </v-btn>
                </v-col>
            </v-row>
        </v-card-title>
        <v-data-table
            :headers="taskGroupHeaders"
            :items="taskGroupItems"
            density="compact"
            no-results-text="No task groups."
            item-key="id"
            class="elevation-1"
            :items-per-page="25"
        >
            <template v-slot:item.isProtocolTaskGroup="{ item }">
                <v-icon>{{ isProtocolTaskGroup(item) ? "$check" : "" }}</v-icon>
            </template>
            <template v-slot:item.edit="{ item }">
                <v-icon v-if="!isReadonly(item)" @click="showEditTaskGroup(item)">$pencil</v-icon>
            </template>
            <template v-slot:item.delete="{ item }">
                <v-icon v-if="!isReadonly(item)" @click="deleteTaskGroup(item)">$delete</v-icon>
            </template>
        </v-data-table>
        <v-dialog
            v-if="isExperimentTaskGroupList()"
            v-model="showTaskGroupFromReferencesForm"
            width="1000"
            persistent
        >
            <task-group-from-references-form
                :protocol="abstractSchemaConfig"
                :experiment="experiment"
                :task-references="taskReferences"
                ref="taskGroupFromReferencesForm"
                @closed="taskGroupFromReferencesFormClosed"
                @saved="taskGroupFromReferencesSaved"
            />
        </v-dialog>
        <v-dialog v-model="showTaskGroupForm" width="1000" persistent>
            <task-group-form
                v-if="selectedTaskGroup !== null"
                :experiment="experiment"
                :task-group="selectedTaskGroup"
                :readout-groups="readoutGroups"
                :task-thing-date-estimates="taskThingDateEstimates"
                @create="createTaskGroup"
                @update="updateTaskGroup"
                @closed="taskGroupFormClosed"
            />
        </v-dialog>
    </v-card>
</template>
<script>
import TaskGroupForm from "./TaskGroupForm.vue";
import { findReadoutGroupsForIds, getDateForTaskGroup, getDayForTaskGroup } from "@/lib/taskUtils";
import TaskGroupFromReferencesForm from "./TaskGroupFromReferencesForm.vue";
import { nextTick, defineComponent } from "vue";
import _ from "lodash";
import { getDateTimeForISODateOnlyString } from "@/lib/dateUtils.js";

export default defineComponent({
    name: "task-group-list",
    components: { TaskGroupFromReferencesForm, TaskGroupForm },

    props: {
        taskReferences: {
            type: Array,
            required: false,
        },
        taskGroups: {
            type: Array,
            required: true,
        },
        readoutGroups: {
            type: Array,
            required: true,
        },
        taskThingDateEstimates: {
            type: Array,
            required: false /* for protocol task groups */,
        },
        abstractSchemaConfig: {
            type: Object,
            required: true,
        },
        experiment: {
            type: Object,
            required: false,
        },
    },

    data() {
        return {
            selectedTaskGroup: null,
            showTaskGroupFromReferencesForm: false,
            showTaskGroupForm: false,
            taskGroupHeaders: [
                {
                    title: "Name",
                    value: "displayName",
                    align: "left",
                    sortable: true,
                    width: "30%",
                },
                {
                    title: "Day",
                    value: "day",
                    align: "left",
                    sortable: true,
                    width: "10%",
                },
                {
                    title: "Date",
                    value: "date",
                    align: "left",
                    sortable: true,
                    width: "15%",
                },
                {
                    title: "Readout Groups",
                    value: "readoutGroups",
                    align: "left",
                    sortable: false,
                    width: "30%",
                },
                {
                    title: "Schema Configuration Task Group",
                    value: "isProtocolTaskGroup",
                    sortable: false,
                    align: "left",
                    width: "5%",
                },
                {
                    title: "Edit",
                    value: "edit",
                    sortable: false,
                    align: "left",
                    width: "5%",
                },
                {
                    title: "Delete",
                    value: "delete",
                    sortable: false,
                    align: "left",
                    width: "5%",
                },
            ],
        };
    },

    computed: {
        taskGroupItems() {
            return this.taskGroups.map((taskGroup) => {
                const readoutGroupsForTask = findReadoutGroupsForIds(
                    taskGroup.readoutGroupIds,
                    this.readoutGroups
                );
                const readoutGroupsDisplay = readoutGroupsForTask
                    .map((readoutGroup) => readoutGroup.name)
                    .join(", ");

                return {
                    ...taskGroup,
                    taskGroup,
                    readoutGroups: readoutGroupsDisplay,
                    day: this.getTaskGroupDay(taskGroup),
                    date: this.getTaskGroupDate(taskGroup),
                };
            });
        },
    },

    methods: {
        isExperimentTaskGroupList() {
            return !_.isNil(this.experiment);
        },
        isReadonly(taskGroup) {
            return this.isExperimentTaskGroupList() && this.isProtocolTaskGroup(taskGroup);
        },
        isProtocolTaskGroup(taskGroup) {
            return _.isNil(taskGroup.experimentCode);
        },
        getTaskGroupDay(taskGroup) {
            if (this.isExperimentTaskGroupList()) {
                return getDayForTaskGroup(taskGroup, this.experiment.day0);
            } else {
                return taskGroup.day;
            }
        },
        getTaskGroupDate(taskGroup) {
            if (this.isExperimentTaskGroupList()) {
                return getDateForTaskGroup(taskGroup);
            } else if (!_.isNil(taskGroup.date)) {
                return getDateTimeForISODateOnlyString(taskGroup.date).toISODate();
            } else {
                return "";
            }
        },
        showCreateTaskGroupsFromReferencesForm() {
            this.showTaskGroupFromReferencesForm = true;
            nextTick(() => {
                // $refs.taskGroupFromReferencesForm isn't available until the v-dialog renders it
                // after the boolean toggle is set.
                this.$refs.taskGroupFromReferencesForm.updated();
            });
        },
        showCreateTaskGroupForm() {
            this.selectedTaskGroup = {
                day: null,
                name: "",
            };
            this.showTaskGroupForm = true;
        },
        showEditTaskGroup(item) {
            this.selectedTaskGroup = item.taskGroup;
            this.showTaskGroupForm = true;
        },
        taskGroupFromReferencesFormClosed() {
            this.showTaskGroupFromReferencesForm = false;
        },
        taskGroupFromReferencesSaved(savedTaskGroups) {
            this.showTaskGroupFromReferencesForm = false;
            this.$emit("saved", savedTaskGroups);
        },
        taskGroupFormClosed() {
            this.resetTaskGroupForm();
        },
        createTaskGroup(taskGroup) {
            this.$emit("create", taskGroup);
            this.resetTaskGroupForm();
        },
        updateTaskGroup(taskGroup) {
            this.$emit("update", taskGroup);
            this.resetTaskGroupForm();
        },
        resetTaskGroupForm() {
            this.selectedTaskGroup = null;
            this.showTaskGroupForm = false;
        },
        deleteTaskGroup(item) {
            const taskGroup = item.taskGroup;
            if (confirm("Are you sure you want to delete this task group?")) {
                this.$emit("delete", taskGroup);
            }
        },
    },
});
</script>
<style></style>
